import React, { useRef, useState, useMemo, useEffect } from "react";
import type { VariantType } from "@shared/graphql.types";
import {
  Text,
  Input,
  FormControl,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogFooter,
  useDisclosure,
  CircularProgress,
  Heading,
  Flex,
  Button,
  FormErrorMessage
} from "@chakra-ui/react";
import { formatOOSDate } from "lib/date";
import { userVar } from "lib/user";
import { useLocale } from "hooks";
import { fonts } from "theme/themeConfig";
import { colours } from "@shared/ui.design-system";

import {
  SignupType,
  trackSegmentNewsLetterSignup
} from "data/tracking/segment/track";
import { NewsletterSignupLocationType } from "data/tracking/segment/helpers";

import {
  checkOOSStorage,
  emailNotification,
  isEmailValid,
  setOOSStorage
} from "./ProductOOS.util";
import { Container, SubmitButton } from "./ProductOOS.style";

type Props = {
  variant: VariantType;
};

export const ProductOOS: React.FC<Props> = ({ variant }) => {
  const user = userVar();
  const [emailAddress, setEmailAddress] = useState(
    user.isLoggedIn ? user?.email : ""
  );
  const [validEmail, setValidEmail] = useState(true);
  const isValidEmail = isEmailValid(emailAddress);
  const cancelRef = useRef(null);
  const { availability, id, sku } = variant;

  const [sentEmail, setSentEmail] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const inStorage = useMemo(() => checkOOSStorage(id ?? ""), [id]);
  const locale = useLocale();

  useEffect(() => {
    setSentEmail(false);
  }, [id]);

  const expectedOn = availability?.expectedOn?.length
    ? formatOOSDate(availability.expectedOn, "regular")
    : null;

  const dueDate = expectedOn ? (
    <Text mt={0} variant="demi0" as="span">
      {`Due back ${expectedOn} `}
    </Text>
  ) : (
    ""
  );

  const getEmailNotification = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    if (!isValidEmail) {
      setValidEmail(false);
      return;
    }
    if (id && sku) {
      setIsLoading(true);
      try {
        await emailNotification(emailAddress, sku, locale);
        setSentEmail(true);
        setOOSStorage(id);
        trackSegmentNewsLetterSignup(
          SignupType.OutOfStock,
          NewsletterSignupLocationType.Page
        );
      } catch (err) {
        onOpen();
      } finally {
        setIsLoading(false);
      }
    }
  };

  const shouldDisplayForm = !variant.badges?.some((badge) =>
    badge?.text?.toLowerCase().includes("sold out")
  );

  const isFormHidden = sentEmail || inStorage;
  if (!shouldDisplayForm) return null;

  return (
    <>
      <AlertDialog
        data-testid="ProductOOSAlertDialog"
        motionPreset="slideInBottom"
        leastDestructiveRef={cancelRef}
        isOpen={isOpen}
        onClose={() => {
          onClose();
        }}
        isCentered
      >
        <AlertDialogOverlay />

        <AlertDialogContent mx="1">
          <AlertDialogHeader>
            <Heading as="h3" variant="demi4">
              Error
            </Heading>
          </AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody py="0">
            <Text variant="book2" pt="0">
              Something went wrong
            </Text>
          </AlertDialogBody>
          <AlertDialogFooter
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Button
              ref={cancelRef}
              size="small"
              onClick={() => onClose()}
              variant="primary"
              data-testid="ModalButtonTryAgain"
            >
              Try again
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Container data-testid="ProductOOSContainer" mb={isFormHidden ? 2 : 0}>
        {isFormHidden ? (
          <Text
            data-testid="ProductOOSInStorage"
            variant="acuminLight0"
            mt={0}
            textAlign="center"
            w="full"
          >
            <Text variant="demi0" as="span">
              We’ll let you know when it’s back in stock
            </Text>
            {expectedOn ? ` (due back ${expectedOn})` : ""}
          </Text>
        ) : (
          <>
            <Text
              data-testid="ProductOOSDueDate"
              mt={0}
              variant="acuminLight0"
              w="full"
              textAlign="left"
            >
              {dueDate}
              Let me know when available*
            </Text>
            <form onSubmit={getEmailNotification}>
              <FormControl
                display="flex"
                mt={-1}
                isInvalid={!isValidEmail}
                flexDirection="column"
              >
                <Flex width="full">
                  <Input
                    required
                    borderStartRadius="2px"
                    height="40px"
                    marginRight="2px"
                    id="oos-email"
                    name="email"
                    variant="form"
                    aria-label="email address"
                    data-testid="emailNotificationInput"
                    value={emailAddress ?? ""}
                    placeholder="example@beautypie.com"
                    onChange={({ target }) => setEmailAddress(target.value)}
                    color="black"
                  />

                  <SubmitButton
                    data-testid="ProductOOSSubmit"
                    as="button"
                    type="submit"
                    aria-labelledby="email"
                    disabled={isLoading}
                  >
                    {!isLoading ? (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M7.63687 4.11653C7.49104 3.96106 7.25479 3.96123 7.10918 4.11693C6.96356 4.27263 6.96373 4.52489 7.10955 4.68036L12.0989 10.0001L7.10955 15.3197C6.96373 15.4752 6.96356 15.7275 7.10918 15.8832C7.25479 16.0389 7.49104 16.0391 7.63687 15.8836L12.8906 10.282C12.963 10.2048 12.9995 10.1037 13 10.0024C13.0006 9.89959 12.9641 9.79654 12.8906 9.71812L7.63687 4.11653Z"
                          fill={colours.secondary.soft}
                        />
                      </svg>
                    ) : (
                      <CircularProgress
                        data-testid="ProductOOSCircularProgress"
                        isIndeterminate
                        color="pink.400"
                        size={6}
                      />
                    )}
                  </SubmitButton>
                </Flex>
                {!validEmail && (
                  <FormErrorMessage mt={-1} fontFamily={fonts.book}>
                    Please enter valid email address.
                  </FormErrorMessage>
                )}
              </FormControl>
            </form>
          </>
        )}
      </Container>

      {!isFormHidden && (
        <Text
          data-testid="ProductOOSInfo"
          w="full"
          textAlign="center"
          mt={-1}
          variant="book0"
          mb={2}
        >
          *We'll only notify you about this item returning.
        </Text>
      )}
    </>
  );
};
